/**
 * CMS Component renderer for the Storyblok Visual Editor preview.
 *
 * Mirrors the mobile-app modal designs exactly so content editors see
 * an accurate representation of what members will see on device.
 *
 * Supported component types:
 *   - <ToolTipPreview>           : story.component === 'tool_tip_content'
 *     Fields: header_text, tool_tip_url (asset), body (richtext/html embed),
 *             copy_right_text, cta_button_text
 *
 *   - <InformationModalPreview>  : story.component === 'information_modal_content'
 *     Fields: body (richtext/html embed), button_label, html_content_height
 *
 * New component types can be added here as additional export functions.
 */

import React from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { storyblokEditable, renderRichText } from '@storyblok/react';
import { isFullHtmlDocument, resolvePreviewHtml, richTextToText, withPreviewDocumentStyles, type StoryblokRichText } from './htmlPreview';

// ── Theme tokens — matches mobile app ToolTipModal colours ────────────
const COLORS = {
  pageBg:       '#F8F7F4',
  cardBg:       '#FFFFFF',
  headerText:   '#0D1C3D',
  bodyText:     '#4A546A',
  ctaBg:        '#1E7FD9',   // matches mobile primary button bg
  ctaText:      '#FFFFFF',
  copyrightText:'#6B7280',
  overlay:      'rgba(0,0,0,0.35)',
};

const CARD_SHADOW = '0px 4px 24px rgba(0,0,0,0.15)';

// ── Types ──────────────────────────────────────────────────────────────

interface StoryblokRel {
  _uid?: string;
  _editable?: string;
}

interface StoryblokAsset {
  filename?: string;
  alt?: string;
}

export interface ToolTipContentBlok extends StoryblokRel {
  component: 'tool_tip_content';
  header_text?: string | { type: string; content?: unknown[] };
  tool_tip_url?: StoryblokAsset;
  body?: Record<string, unknown> | string; // richtext object or plain string
  copy_right_text?: string | { type: string; content?: unknown[] };
  cta_button_text?: string | { type: string; content?: unknown[] };
}

// ── Helpers ───────────────────────────────────────────────────────────

function editableProps(blok: StoryblokRel | undefined): Record<string, string> {
  if (!blok) return {};
  return storyblokEditable(
    blok as unknown as Parameters<typeof storyblokEditable>[0],
  ) as Record<string, string>;
}

/**
 * Convert a Storyblok richtext body to an HTML string.
 * Handles: plain string, richtext with embedded html/code_block nodes,
 * and standard Storyblok richtext (paragraphs, bullet lists, etc.).
 */
function resolveBodyHtml(body: Record<string, unknown> | string | undefined): string {
  return resolvePreviewHtml(body);
}

// ── ToolTip preview ────────────────────────────────────────────────────
//
// Layout mirrors the mobile ToolTipModal:
//   [X close]
//   <header_text>          ← left-aligned, bold
//   <body iframe>          ← full-height iframe for HTML doc, or inline HTML
//   <copy_right_text>      ← small grey text (only when NOT part of body HTML)
//   [  cta_button_text  ]  ← full-width blue rounded button

/**
 * Safely extracts a displayable string from a field that may be either a
 * plain string or a Storyblok richtext object {type:'doc', content:[...]}.
 * Prevents "Objects are not valid as React child" crashes in the preview.
 */
function safeText(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    // Richtext doc — render to HTML and strip tags for plain display
    try {
      const html = String(renderRichText(val as unknown as Parameters<typeof renderRichText>[0]) ?? '');
      return html.replace(/<[^>]*>/g, '').trim();
    } catch {
      return '';
    }
  }
  return String(val);
}

/** Like safeText but preserves richtext HTML formatting (bold, italic, etc.) */
function safeHtml(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    try {
      return String(renderRichText(val as unknown as Parameters<typeof renderRichText>[0]) ?? '');
    } catch {
      return '';
    }
  }
  return String(val);
}

// ── CtaCardBanner types ─────────────────────────────────────────────────

interface CtaBannerStatusBlok extends StoryblokRel {
  component: 'cta_banner_status';
  status_key?: string;     // e.g. ELIGIBLE_TO_ORDER, FIS_BATCH_SENT, etc.
  banner_header?: string | { type: string; content?: unknown[] };
  banner_description?: string | { type: string; content?: unknown[] };
}

export interface CtaCardBannerContentBlok extends StoryblokRel {
  component: 'cta_card_banner_content';
  statuses?: CtaBannerStatusBlok[];
}

// ── BenefitsCardArt types ───────────────────────────────────────────────

export interface BenefitsCardArtContentBlok extends StoryblokRel {
  component: 'benefits_card_art_content';
  card_art_url?: StoryblokAsset;
  copy_right_text?: string | { type: string; content?: unknown[] };
}

// ── InformationModal types ──────────────────────────────────────────────

export interface InformationModalContentBlok extends StoryblokRel {
  component: 'information_modal_content';
  body?: Record<string, unknown> | string; // richtext object or plain string
  button_label?: string | { type: string; content?: unknown[] };
  html_content_height?: number;
}

export interface ToolTipPreviewProps {
  blok: ToolTipContentBlok;
}

export function ToolTipPreview({ blok }: ToolTipPreviewProps) {
  const bodyHtml     = resolveBodyHtml(blok.body);
  const useIframe    = bodyHtml ? isFullHtmlDocument(bodyHtml) : false;
  const ctaHtml      = resolvePreviewHtml(blok.cta_button_text as any) || 'Got it';
  const copyrightTxt = safeText(blok.copy_right_text);

  return (
    // Page background — simulates the dimmed screen behind the modal
    <Box
      sx={{
        bgcolor: COLORS.overlay,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >
      {/* Modal card — matches ToolTipModal styles exactly */}
      <Box
        {...editableProps(blok)}
        sx={{
          bgcolor: COLORS.cardBg,
          borderRadius: '16px',
          boxShadow: CARD_SHADOW,
          width: '100%',
          maxWidth: 480,
          pt: 1.5,
          pb: 3,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}
      >
        {/* Sticky close row — matches stickyHeader in ToolTipModal */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: 40,
            mb: 0,
          }}
        >
          <IconButton size="small" disabled sx={{ color: COLORS.headerText }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Header — left-aligned bold, matches titleText in ToolTipModal */}
        {blok.header_text && (
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 20,
              color: COLORS.headerText,
              mb: 2,
              px: 0,
              lineHeight: 1.3,
              fontFamily: 'Manrope, sans-serif',
            }}
            dangerouslySetInnerHTML={{ __html: resolvePreviewHtml(blok.header_text as any) ?? '' }}
          />
        )}

        {/* Body — iframe for full HTML doc, inline HTML otherwise */}
        {bodyHtml ? (
          useIframe ? (
            <Box sx={{ width: '100%', height: 320, mb: 1 }}>
              <iframe
                srcDoc={withPreviewDocumentStyles(bodyHtml)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="tooltip body"
                sandbox="allow-scripts"
              />
            </Box>
          ) : (
            <Box
              sx={{ fontSize: 14, color: COLORS.bodyText, lineHeight: 1.7, mb: 1 }}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )
        ) : (
          <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontStyle: 'italic', mb: 1 }}>
            (no body content yet)
          </Typography>
        )}

        {/* Copyright — only shown when body does NOT already include it */}
        {copyrightTxt && !useIframe && (
          <Typography
            sx={{
              fontSize: 14,
              color: COLORS.copyrightText,
              '& p': { margin: 0 },
              mt: 1,
              mb: 2,
              fontFamily: 'Manrope, sans-serif',
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml(blok.copy_right_text) }}
          />
        )}

        {/* CTA button — matches BFButton in ToolTipModal renderButton() */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              bgcolor: COLORS.ctaBg,
              color: COLORS.ctaText,
              borderRadius: 80,
              py: 1.75,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: 'Manrope, sans-serif',
              textTransform: 'none',
              '&:hover': { bgcolor: '#1a6bbf' },
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: ctaHtml }} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── InformationModal preview ───────────────────────────────────────────
//
// Layout mirrors SweepStakesInfoModal on the Rewards screen:
//   [X close]
//   <body iframe>        ← full-height iframe (URL in legacy; srcDoc in Storyblok)
//   [  button_label  ]   ← full-width blue rounded button

export interface InformationModalPreviewProps {
  blok: InformationModalContentBlok;
}

export function InformationModalPreview({ blok }: InformationModalPreviewProps) {
  const bodyHtml    = resolveBodyHtml(blok.body);
  const buttonLabelHtml = resolvePreviewHtml(blok.button_label as any) || 'Got it';

  return (
    // Page background — simulates the dimmed screen behind the modal.
    <Box
      sx={{
        bgcolor: COLORS.overlay,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 2,
      }}
    >
      {/* Modal card — explicit height so flex children can fill remaining space */}
      <Box
        {...editableProps(blok)}
        sx={{
          bgcolor: COLORS.cardBg,
          borderRadius: '16px',
          boxShadow: CARD_SHADOW,
          width: '100%',
          maxWidth: 480,
          height: '85vh',
          pt: 1.5,
          pb: 3,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: 40,
            flexShrink: 0,
          }}
        >
          <IconButton size="small" disabled sx={{ color: COLORS.headerText }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body — iframe grows to fill all remaining card height */}
        {bodyHtml ? (
          <Box sx={{ flex: 1, width: '100%', minHeight: 0, mb: 2 }}>
            <iframe
              srcDoc={withPreviewDocumentStyles(bodyHtml)}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="information modal body"
              sandbox="allow-scripts"
            />
          </Box>
        ) : (
          <Typography
            sx={{ flex: 1, fontSize: 14, color: COLORS.bodyText, fontStyle: 'italic', mb: 2 }}
          >
            (no body content yet)
          </Typography>
        )}

        {/* Button — matches BFButton in SweepStakesInfoModal */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              bgcolor: COLORS.ctaBg,
              color: COLORS.ctaText,
              borderRadius: 80,
              py: 1.75,
              fontSize: 16,
              fontWeight: 600,
              fontFamily: 'Manrope, sans-serif',
              textTransform: 'none',
              '&:hover': { bgcolor: '#1a6bbf' },
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: buttonLabelHtml }} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── BenefitsCardArt preview ────────────────────────────────────────────
//
// Shows the card art image + copyright text, mirroring how MyCard /
// WebSpendScreen / ShopV1/V2 render the component.

export interface BenefitsCardArtPreviewProps {
  blok: BenefitsCardArtContentBlok;
}

export function BenefitsCardArtPreview({ blok }: BenefitsCardArtPreviewProps) {
  const cardArtUrl   = blok.card_art_url?.filename;
  const copyrightTxt = safeText(blok.copy_right_text);

  return (
    <Box
      sx={{
        bgcolor: COLORS.pageBg,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        p: 4,
      }}
    >
      {/* Card art image */}
      {cardArtUrl ? (
        <Box
          {...editableProps(blok)}
          component="img"
          src={cardArtUrl}
          alt={blok.card_art_url?.alt || 'Benefits card art'}
          sx={{
            maxWidth: 360,
            width: '100%',
            borderRadius: '12px',
            boxShadow: CARD_SHADOW,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 360,
            height: 220,
            borderRadius: '12px',
            bgcolor: '#E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: CARD_SHADOW,
          }}
        >
          <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontStyle: 'italic' }}>
            (no card art image yet)
          </Typography>
        </Box>
      )}

      {/* Copyright text */}
      {copyrightTxt && (
        <Typography
          sx={{
            fontSize: 12,
            color: COLORS.copyrightText,
            textAlign: 'center',
            maxWidth: 360,
            fontFamily: 'Manrope, sans-serif',
            '& p': { margin: 0 },
          }}
          dangerouslySetInnerHTML={{ __html: safeHtml(blok.copy_right_text) }}
        />
      )}
    </Box>
  );
}

// ── ShopOnlineCarousel types ───────────────────────────────────────────

interface CarouselItemBlok extends StoryblokRel {
  component: 'carousel_item';
  sort_key?: string;
  image?: StoryblokAsset;
  heading?: string;
  subheading?: string;
  button_label?: string;
  html_content?: string;
  button_cta_url?: string;
  terms_label?: string;
  terms_html_content?: string;
  terms_modal_heading?: string;
}

export interface ShopOnlineCarouselContentBlok extends StoryblokRel {
  component: 'shop_online_carousel_content';
  items?: CarouselItemBlok[];
}

// ── ShopOnlineCarousel preview ─────────────────────────────────────────
//
// Renders each carousel_item as a stacked card showing all fields —
// mirrors the card layout the mobile app renders per slide.

export interface ShopOnlineCarouselPreviewProps {
  blok: ShopOnlineCarouselContentBlok;
}

export function ShopOnlineCarouselPreview({ blok }: ShopOnlineCarouselPreviewProps) {
  // Sort by sort_key ascending (mirrors mobile-app mapper)
  const items = [...(blok.items ?? [])].sort((a, b) => {
    const na = Number(a.sort_key);
    const nb = Number(b.sort_key);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a.sort_key ?? '').localeCompare(String(b.sort_key ?? ''));
  });

  if (items.length === 0) {
    return (
      <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
        <Typography sx={{ fontSize: 13, color: COLORS.copyrightText, fontStyle: 'italic', fontFamily: 'Manrope, sans-serif' }}>
          No items added yet. Add <code>carousel_item</code> blocks.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Constrain to mobile card width */}
      <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* Slide count label */}
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif' }}>
        Shop Online Carousel — {items.length} slide{items.length !== 1 ? 's' : ''}
      </Typography>

      {items.map((item, i) => (
        <Box
          key={item._uid || i}
          {...editableProps(item)}
          sx={{
            bgcolor: COLORS.cardBg,
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Slide number pill */}
          <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center',
              bgcolor: '#1E7FD9', borderRadius: '6px', px: 1.25, py: 0.4,
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Manrope, sans-serif' }}>
                Slide {safeText(item.sort_key) || i + 1}
              </Typography>
            </Box>
          </Box>

          {/* Image */}
          {item.image?.filename ? (
            <Box
              component="img"
              src={item.image.filename}
              alt={item.image.alt || safeText(item.heading) || ''}
              sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block', mt: 1.5 }}
            />
          ) : (
            <Box sx={{ width: '100%', height: 140, bgcolor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontStyle: 'italic', fontFamily: 'Manrope, sans-serif' }}>(no image)</Typography>
            </Box>
          )}

          {/* Text content */}
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {item.heading && (
              <Typography sx={{ fontWeight: 700, fontSize: 20, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', lineHeight: 1.3, '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(item.heading) }} />
            )}
            {item.subheading && (
              <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', lineHeight: 1.6, '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(item.subheading) }} />
            )}
          </Box>

          {/* Footer: CTA + terms + dots */}
          <Box sx={{ px: 3, pt: 1, pb: 3, display: 'flex', flexDirection: 'column', gap: 1.5, mt: 'auto' }}>
            {item.button_label && (
              <Button
                variant="contained"
                disableElevation
                fullWidth
                component="a"
                href={safeText(item.button_cta_url) || undefined}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: '#1E7FD9',
                  color: '#fff',
                  borderRadius: 80,
                  py: 1.5,
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: 'Manrope, sans-serif',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1a6bbf' },
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: safeHtml(item.button_label) }} />
              </Button>
            )}

            {item.terms_label && (
              <Typography sx={{ fontSize: 13, color: '#1E7FD9', textAlign: 'center', fontFamily: 'Manrope, sans-serif', fontWeight: 600, '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(item.terms_label) }} />
            )}

            {/* Pagination dots (decorative — shows relative position in slide order) */}
            {items.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 0.5 }}>
                {items.map((_, dotIdx) => (
                  <Box
                    key={dotIdx}
                    sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: dotIdx === i ? '#374151' : '#D1D5DB',
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      ))}
      </Box>
    </Box>
  );
}

// ── CtaCardBanner preview ──────────────────────────────────────────────
//
// Renders each cta_banner_status block as a grouped card — mirrors the
// legacy CMS "grouped by status" layout the editors are used to.
// On device only one banner shows at a time (driven by cardIssueStatus).

export interface CtaCardBannerPreviewProps {
  blok: CtaCardBannerContentBlok;
}

export function CtaCardBannerPreview({ blok }: CtaCardBannerPreviewProps) {
  const statuses = blok.statuses ?? [];

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.headerText, mb: 1, fontFamily: 'Manrope, sans-serif' }}>
        CTA Card Banner — all statuses
      </Typography>

      {statuses.length === 0 && (
        <Typography sx={{ fontSize: 13, color: COLORS.copyrightText, fontStyle: 'italic' }}>
          No statuses added yet. Add <code>cta_banner_status</code> blocks.
        </Typography>
      )}

      {statuses.map((s, i) => (
        <Box
          key={s._uid || i}
          {...editableProps(s)}
          sx={{ bgcolor: '#E9EBEE', borderRadius: '12px', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          {/* Status key label — matches the group header in legacy CMS */}
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.copyrightText, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
            dangerouslySetInnerHTML={{ __html: safeHtml(s.status_key) || '(no status_key)' }} />
          {s.banner_header && (
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
              dangerouslySetInnerHTML={{ __html: safeHtml(s.banner_header) }} />
          )}
          <Typography sx={{ fontSize: 13, color: s.banner_description ? COLORS.bodyText : COLORS.copyrightText, fontStyle: s.banner_description ? 'normal' : 'italic', fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
            dangerouslySetInnerHTML={{ __html: safeHtml(s.banner_description) || '(no banner_description)' }} />
        </Box>
      ))}
    </Box>
  );
}

// ── OnboardingSplash types ──────────────────────────────────────────────

interface OnboardingAgreementRefBlok extends StoryblokRel {
  component?: string; // onboarding_agreement_ref / agreement_ref
  agreement_page_code?: string;
  display_name?: string;
}

export interface OnboardingSplashContentBlok extends StoryblokRel {
  component: 'onboarding_splash_content';
  header_text?: string | { type: string; content?: unknown[] };
  description?: string | { type: string; content?: unknown[] };
  disclaimer?: string | { type: string; content?: unknown[] };
  cta_button_text?: string | { type: string; content?: unknown[] };
  copy_right_text?: string | { type: string; content?: unknown[] };
  header_text_alignment?: string;                 // Left | Center | Right
  html_content?: Record<string, unknown> | string; // richtext (optional rich body)
  graphic_url_mobile?: StoryblokAsset;             // portrait crop (phones)
  graphic_url_desktop?: StoryblokAsset;            // landscape crop (web)
  agreements?: OnboardingAgreementRefBlok[];
}

// ── OnboardingSplash preview ────────────────────────────────────────────
//
// Mirrors the mobile RewardsSplash onboarding screen: graphic, aligned
// header, description, optional rich body, disclaimer + agreement links,
// CTA button, and copyright footer.

export interface OnboardingSplashPreviewProps {
  blok: OnboardingSplashContentBlok;
}

export function OnboardingSplashPreview({ blok }: OnboardingSplashPreviewProps) {
  // Mirrors the mobile RewardsSplash *phone* layout: a full white screen with
  // a full-width 384px-tall graphic, LEFT-aligned header/description (the
  // header_text_alignment field only affects desktop web; phones are always
  // left), and a tall full-width pill CTA. Prefer the mobile (portrait) crop.
  const graphicAsset = blok.graphic_url_mobile?.filename ? blok.graphic_url_mobile : blok.graphic_url_desktop;
  const graphic   = graphicAsset?.filename;
  const bodyHtml  = resolveBodyHtml(blok.html_content);
  const useIframe = bodyHtml ? isFullHtmlDocument(bodyHtml) : false;
  const ctaHtml   = resolvePreviewHtml(blok.cta_button_text as any) || 'Continue';
  const agreements = blok.agreements ?? [];

  const HEADER_COLOR = '#0D1C3D';   // tenantUxthemeColors.headerBgColor default

  return (
    // Light backdrop simulates the device frame; the splash itself is a full
    // white screen (commonColors.splashScreenBgColor || white on device).
    <Box sx={{ bgcolor: '#ECECEC', minHeight: '100vh', py: 4, display: 'flex', justifyContent: 'center' }}>
      <Box
        {...editableProps(blok)}
        sx={{
          bgcolor: '#FFFFFF',
          width: 390,
          maxWidth: '100%',
          minHeight: 760,
          px: 2,
          pt: 2.5,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
        }}
      >
        {/* Graphic — full width, fixed 384 height (matches styles.image) */}
        {graphic ? (
          <Box component="img" src={graphic} alt={graphicAsset?.alt || 'Onboarding graphic'}
            sx={{ width: '100%', height: 384, objectFit: 'cover', display: 'block', mb: 4 }} />
        ) : (
          <Box sx={{ width: '100%', height: 384, bgcolor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontStyle: 'italic', textAlign: 'center' }}>(no graphic — set graphic_url_mobile)</Typography>
          </Box>
        )}

        {/* Header — x-large-bold, left aligned (phone) */}
        {blok.header_text && (
          <Typography
            sx={{ fontWeight: 800, fontSize: 26, color: HEADER_COLOR, textAlign: 'left', lineHeight: 1.25, fontFamily: 'Manrope, sans-serif' }}
            dangerouslySetInnerHTML={{ __html: resolvePreviewHtml(blok.header_text as any) ?? '' }}
          />
        )}

        {/* Description — "small" variant, left aligned, mt 16 */}
        {blok.description && (
          <Typography
            sx={{ fontSize: 15, color: HEADER_COLOR, textAlign: 'left', mt: 2, lineHeight: 1.6, fontFamily: 'Manrope, sans-serif' }}
            dangerouslySetInnerHTML={{ __html: resolvePreviewHtml(blok.description as any) ?? '' }}
          />
        )}

        {/* Optional rich body (html_content) */}
        {bodyHtml && (
          useIframe ? (
            <Box sx={{ width: '100%', height: 280, mt: 2 }}>
              <iframe srcDoc={withPreviewDocumentStyles(bodyHtml)} style={{ width: '100%', height: '100%', border: 'none' }} title="onboarding body" sandbox="allow-scripts" />
            </Box>
          ) : (
            <Box sx={{ width: '100%', mt: 2, fontSize: 15, color: HEADER_COLOR, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          )
        )}

        {/* Spacer — pushes disclaimer + CTA toward the bottom, like the screen */}
        <Box sx={{ flex: 1, minHeight: 24 }} />

        {/* Disclaimer + agreement links — small, left aligned */}
        {(blok.disclaimer || agreements.length > 0) && (
          <Typography sx={{ fontSize: 13, color: HEADER_COLOR, textAlign: 'left', lineHeight: 1.6, mb: 2.5, fontFamily: 'Manrope, sans-serif' }}>
            <span dangerouslySetInnerHTML={{ __html: resolvePreviewHtml(blok.disclaimer as any) ?? '' }} />{' '}
            {agreements.map((a, i) => (
              <React.Fragment key={a._uid || i}>
                <Box component="span" sx={{ color: COLORS.ctaBg, textDecoration: 'underline' }}>
                  {a.display_name || a.agreement_page_code || '(unnamed agreement)'}
                </Box>
                {i < agreements.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </Typography>
        )}

        {/* "out of schema" hint — agreement entries with no page_code resolve to nothing on device */}
        {agreements.some((a) => !a.agreement_page_code) && (
          <Typography sx={{ fontSize: 11, color: '#B45309', fontStyle: 'italic', mb: 1 }}>
            ⚠ An agreement has no agreement_page_code — it won&apos;t resolve to a body on device.
          </Typography>
        )}

        {/* CTA — full-width pill, height 64 (matches styles.button) */}
        <Button fullWidth variant="contained" disableElevation
          sx={{ bgcolor: COLORS.ctaBg, color: COLORS.ctaText, borderRadius: '80px', height: 64, fontSize: 16, fontWeight: 600, fontFamily: 'Manrope, sans-serif', textTransform: 'none', '&:hover': { bgcolor: '#1a6bbf' } }}>
          <span dangerouslySetInnerHTML={{ __html: ctaHtml }} />
        </Button>

        {/* Copyright — centered grey footer */}
        {blok.copy_right_text && (
          <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, textAlign: 'center', mt: 2, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
            dangerouslySetInnerHTML={{ __html: safeHtml(blok.copy_right_text) }} />
        )}
      </Box>
    </Box>
  );
}

// ── PurseDetails types ──────────────────────────────────────────────────

interface PurseDetailItemBlok extends StoryblokRel {
  component?: string; // purse_detail
  wallet_type_code?: string;
  wallet_type_id?: string; // legacy field name
  wallet_type_label?: string;
  purse_image?: StoryblokAsset;
  purse_inactive_image?: StoryblokAsset;
  see_more_icon?: StoryblokAsset;
  purse_url?: StoryblokAsset; // legacy field name
  expires_header?: string;
  expires_text?: string;
  annual_allowance_header?: string;
  annual_allowance_text?: string;
  restrictions_header?: string | { type: string; content?: unknown[] };
  restrictions?: string | { type: string; content?: unknown[] };
  see_more_header?: string | { type: string; content?: unknown[] };
  see_more_body?: Record<string, unknown> | string; // richtext
  purse_shop_description?: string;
  purse_shop_label?: string;
}

export interface PurseDetailsContentBlok extends StoryblokRel {
  component: 'purse_details_content';
  items?: PurseDetailItemBlok[];
}

// ── PurseDetails preview ────────────────────────────────────────────────
//
// Mirrors the Shop screen's per-wallet purse cards: each item shows the purse
// icon, the wallet-type label, a shop description, and a CTA label. One card
// per wallet type (matched on device by wallet_type_id / wallet_type_label).

export interface PurseDetailsPreviewProps {
  blok: PurseDetailsContentBlok;
}

export function PurseDetailsPreview({ blok }: PurseDetailsPreviewProps) {
  const items = blok.items ?? [];
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif' }}>
          Purse Details — {items.length} wallet type{items.length !== 1 ? 's' : ''}
        </Typography>
        {items.length === 0 && (
          <Typography sx={{ fontSize: 13, color: COLORS.copyrightText, fontStyle: 'italic' }}>
            No purses yet. Add <code>purse_detail</code> blocks (wallet_type_id, wallet_type_label, purse_url, purse_shop_description, purse_shop_label).
          </Typography>
        )}
        {items.map((it, i) => {
          const icon = (it.purse_image ?? it.purse_url)?.filename;
          const code = safeText(it.wallet_type_code) || safeText(it.wallet_type_id);
          const restrictionsHtml = safeHtml(it.restrictions);
          const restrictionsText = safeText(it.restrictions);
          const row = (label: any, value: any) => {
            if (!safeText(label) || !safeText(value)) return null;
            return (
              <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontFamily: 'Manrope, sans-serif' }} dangerouslySetInnerHTML={{ __html: safeHtml(label) }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif' }} dangerouslySetInnerHTML={{ __html: safeHtml(value) }} />
              </Box>
            );
          };
          return (
            <Box key={it._uid || i} {...editableProps(it)}
              sx={{ bgcolor: COLORS.cardBg, borderRadius: '14px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
              {icon ? (
                <Box component="img" src={icon} alt={(it.purse_image ?? it.purse_url)?.alt || safeText(it.wallet_type_label) || 'Purse'} sx={{ width: 64, height: 64, objectFit: 'contain' }} />
              ) : (
                <Box sx={{ width: 64, height: 64, borderRadius: '8px', bgcolor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 9, color: COLORS.copyrightText, fontStyle: 'italic', textAlign: 'center' }}>no icon</Typography>
                </Box>
              )}
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.copyrightText, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(it.wallet_type_label) || code || '(unidentified wallet)' }} />
              {it.purse_shop_description && (
                <Typography sx={{ fontSize: 13, color: COLORS.bodyText, textAlign: 'center', fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                  dangerouslySetInnerHTML={{ __html: safeHtml(it.purse_shop_description) }} />
              )}
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {row(it.expires_header, it.expires_text)}
                {row(it.annual_allowance_header, it.annual_allowance_text)}
              </Box>
              {restrictionsText && (
                <Box sx={{ width: '100%' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.copyrightText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                    dangerouslySetInnerHTML={{ __html: safeHtml(it.restrictions_header) || 'Restrictions' }} />
                  <Box sx={{ fontSize: 12, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 }, '& ul': { margin: 0, paddingLeft: 20 }, '& li': { marginBottom: 2 } }}
                    dangerouslySetInnerHTML={{ __html: restrictionsHtml }} />
                </Box>
              )}
              <Button variant="outlined" disableElevation
                sx={{ mt: 0.5, borderRadius: 80, textTransform: 'none', fontFamily: 'Manrope, sans-serif', borderColor: COLORS.ctaBg, color: COLORS.ctaBg }}>
                <span dangerouslySetInnerHTML={{ __html: safeHtml(it.purse_shop_label) || 'Shop' }} />
              </Button>
              {(it.see_more_header || it.see_more_body) && (
                <Typography sx={{ fontSize: 11, color: COLORS.ctaBg, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}>
                  <span dangerouslySetInnerHTML={{ __html: safeHtml(it.see_more_header) || 'See more' }} /> ↗
                </Typography>
              )}
              {!code && !it.wallet_type_label && (
                <Typography sx={{ fontSize: 10, color: '#B45309', fontStyle: 'italic' }}>
                  ⚠ needs wallet_type_code or wallet_type_label to bind to a wallet on device
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── CohortSpecificContent types ─────────────────────────────────────────

export interface CohortSpecificContentBlok extends StoryblokRel {
  component: 'cohort_specific_content';
  activate_card_screen_message?: string | { type: string; content?: unknown[] };
}

// ── CohortSpecificContent preview ───────────────────────────────────────
//
// Mirrors the Activate-Card modal: a title + the cohort-specific message +
// Activate / Skip buttons. Only activate_card_screen_message is authored here;
// the rest is app i18n. Cohort resolution happens server-side.

export interface CohortSpecificContentPreviewProps {
  blok: CohortSpecificContentBlok;
}

export function CohortSpecificContentPreview({ blok }: CohortSpecificContentPreviewProps) {
  const msg = safeText(blok.activate_card_screen_message);
  return (
    <Box sx={{ bgcolor: COLORS.overlay, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box {...editableProps(blok)}
        sx={{ bgcolor: COLORS.cardBg, borderRadius: '16px', boxShadow: CARD_SHADOW, width: '100%', maxWidth: 420, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 20, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif' }}>
          Activate your card
        </Typography>
        <Typography sx={{ fontSize: 14, color: msg ? COLORS.bodyText : COLORS.copyrightText, fontStyle: msg ? 'normal' : 'italic', lineHeight: 1.6, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(blok.activate_card_screen_message) || '(no activate_card_screen_message — the app falls back to its default message)' }} />
        <Button fullWidth variant="contained" disableElevation
          sx={{ bgcolor: COLORS.ctaBg, color: COLORS.ctaText, borderRadius: 80, py: 1.5, fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif', textTransform: 'none' }}>
          Activate
        </Button>
        <Button fullWidth variant="text"
          sx={{ color: COLORS.ctaBg, borderRadius: 80, py: 1.25, fontSize: 15, fontWeight: 600, fontFamily: 'Manrope, sans-serif', textTransform: 'none' }}>
          Skip
        </Button>
      </Box>
    </Box>
  );
}

// ── UILanguageResource types ────────────────────────────────────────────

export interface TranslationEntryBlok extends StoryblokRel {
  component?: 'translation_entry';
  // Both may be richtext docs since CPT-1254; read them through richTextToText.
  key?: StoryblokRichText;
  value?: StoryblokRichText; // dimension-resolved value for the language selected in the editor
}

export interface UILanguageResourceContentBlok extends StoryblokRel {
  component: 'ui_language_resource_content';
  translations?: TranslationEntryBlok[];
}

// ── UILanguageResource preview ──────────────────────────────────────────
//
// Readable, grouped, searchable view of the tenant's UI string dictionary. Each
// translation_entry is one i18n key; `value` is the string for the language
// currently selected in the Storyblok editor (the translatable dimension). Rows
// are grouped by their top-level key segment (e.g. "rewards_splash"). Clicking a
// row opens that entry's key/value fields directly in the Storyblok side panel
// (storyblokEditable) — and switching the editor's language menu re-renders each
// row with that language's value, since it's one translatable field per key.

export interface UILanguageResourcePreviewProps {
  blok: UILanguageResourceContentBlok;
}

export function UILanguageResourcePreview({ blok }: UILanguageResourcePreviewProps) {
  const [query, setQuery] = React.useState('');
  // key/value may be plain strings or (CPT-1254) richtext docs — flatten once for
  // search, grouping and the key column; the value cell still renders formatting.
  const entries = (blok.translations ?? [])
    .map((t) => ({ entry: t, keyText: richTextToText(t?.key).trim(), valueText: richTextToText(t?.value).trim() }))
    .filter((t) => t.keyText.length > 0);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? entries.filter(
        (t) => t.keyText.toLowerCase().includes(q) || t.valueText.toLowerCase().includes(q),
      )
    : entries;

  // Group by top-level section (first dotted segment of the key).
  const groups: Record<string, typeof entries> = {};
  for (const t of filtered) {
    const section = t.keyText.split('.')[0] || '(root)';
    (groups[section] = groups[section] || []).push(t);
  }
  const sections = Object.keys(groups).sort();

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mb: 0.5 }}>
          UI Language Resource
        </Typography>
        <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontFamily: 'Manrope, sans-serif', mb: 2 }}>
          {entries.length} keys · showing {filtered.length}. Click any row to edit it in Storyblok. Switch
          the language (en / es) in the editor's language menu to edit each translation of the same key.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search keys or values…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2, bgcolor: COLORS.cardBg, '& .MuiOutlinedInput-root': { fontFamily: 'Manrope, sans-serif', fontSize: 13 } }}
        />

        {entries.length === 0 && (
          <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: COLORS.copyrightText, fontFamily: 'Manrope, sans-serif' }}>
            (no translation rows yet — this story hasn&apos;t been prefilled)
          </Typography>
        )}

        {sections.map((section) => (
          <Box key={section} sx={{ mb: 2.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: COLORS.ctaBg, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: 'Manrope, sans-serif', mb: 0.75 }}>
              {section}{' '}
              <Box component="span" sx={{ color: COLORS.copyrightText, fontWeight: 500 }}>
                · {groups[section].length}
              </Box>
            </Typography>
            <Box sx={{ bgcolor: COLORS.cardBg, borderRadius: '12px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
              {groups[section].map(({ entry: t, keyText, valueText: val }, i) => {
                const valueIsDoc = !!t.value && typeof t.value === 'object';
                return (
                  <Box
                    key={t._uid || keyText || i}
                    {...editableProps(t)}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      p: 1.25,
                      cursor: 'pointer',
                      borderTop: i === 0 ? 'none' : `1px solid ${COLORS.pageBg}`,
                      '&:hover': { bgcolor: COLORS.pageBg },
                    }}
                  >
                    <Typography sx={{ flex: '0 0 42%', fontFamily: 'monospace', fontSize: 12, color: COLORS.bodyText, wordBreak: 'break-all' }}>
                      {keyText}
                    </Typography>
                    {val && valueIsDoc ? (
                      <Typography sx={{ flex: 1, fontSize: 13, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                        dangerouslySetInnerHTML={{ __html: safeHtml(t.value) }} />
                    ) : (
                      <Typography sx={{ flex: 1, fontSize: 13, color: val ? COLORS.headerText : COLORS.copyrightText, fontStyle: val ? 'normal' : 'italic', fontFamily: 'Manrope, sans-serif', whiteSpace: 'pre-wrap' }}>
                        {val || '(empty)'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Shared: labelled image thumbnail (used by trivia / sweepstakes / maximize) ──
function ImageThumb({ label, asset, w = 132, h = 92 }: { label: string; asset?: StoryblokAsset; w?: number; h?: number }) {
  const url = asset?.filename;
  return (
    <Box sx={{ width: w }}>
      <Box sx={{ width: w, height: h, borderRadius: '10px', border: '1px solid #E5E7EB', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {url
          ? <Box component="img" src={url} alt={label} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <Typography sx={{ fontSize: 11, color: COLORS.copyrightText, fontStyle: 'italic' }}>empty</Typography>}
      </Box>
      <Typography sx={{ fontSize: 11, color: COLORS.bodyText, mt: 0.5, fontFamily: 'Manrope, sans-serif' }}>{label}</Typography>
    </Box>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Typography sx={{ fontSize: 13, color: COLORS.copyrightText, fontStyle: 'italic', fontFamily: 'Manrope, sans-serif' }}>
        {children}
      </Typography>
    </Box>
  );
}

// ── Trivia Card preview ────────────────────────────────────────────────
export interface TriviaCardContentBlok extends StoryblokRel {
  component: 'trivia_card_content';
  header_text?: string;
  description_text?: string;
  question_screen_background_image?: StoryblokAsset;
  question_screen_correct_image?: StoryblokAsset;
  question_screen_wrong_image?: StoryblokAsset;
  question_screen_question_image?: StoryblokAsset;
  success_screen_background_image?: StoryblokAsset;
  success_screen_correct_image?: StoryblokAsset;
  success_screen_wrong_image?: StoryblokAsset;
  success_screen_top_graphic?: StoryblokAsset;
  success_screen_bottom_logo?: StoryblokAsset;
}
export interface TriviaCardPreviewProps { blok: TriviaCardContentBlok; }

export function TriviaCardPreview({ blok }: TriviaCardPreviewProps) {
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Box {...editableProps(blok)} sx={{ maxWidth: 720, mx: 'auto' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 20, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(blok.header_text) || 'Trivia' }} />
        <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', mb: 2.5, '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(blok.description_text) || '(no description)' }} />

        <Typography sx={{ fontWeight: 700, fontSize: 12, color: COLORS.ctaBg, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Question screen</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <ImageThumb label="Background" asset={blok.question_screen_background_image} />
          <ImageThumb label="Question" asset={blok.question_screen_question_image} />
          <ImageThumb label="Correct" asset={blok.question_screen_correct_image} />
          <ImageThumb label="Wrong" asset={blok.question_screen_wrong_image} />
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 12, color: COLORS.ctaBg, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Success screen</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <ImageThumb label="Background" asset={blok.success_screen_background_image} />
          <ImageThumb label="Top graphic" asset={blok.success_screen_top_graphic} />
          <ImageThumb label="Correct" asset={blok.success_screen_correct_image} />
          <ImageThumb label="Wrong" asset={blok.success_screen_wrong_image} />
          <ImageThumb label="Bottom logo" asset={blok.success_screen_bottom_logo} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Maximize-Your-Benefits preview ─────────────────────────────────────
interface MaximizeItemBlok extends StoryblokRel {
  component: 'maximize_your_benefits_item';
  header_text?: string;
  description_text?: string;
  background_image_url?: StoryblokAsset;
  video_url?: string;
}
export interface MaximizeYourBenefitsContentBlok extends StoryblokRel {
  component: 'maximize_your_benefits_content';
  items?: MaximizeItemBlok[];
}
export interface MaximizeYourBenefitsPreviewProps { blok: MaximizeYourBenefitsContentBlok; }

export function MaximizeYourBenefitsPreview({ blok }: MaximizeYourBenefitsPreviewProps) {
  const items = blok.items ?? [];
  if (items.length === 0) return <EmptyState>No cards yet. Add <code>maximize_your_benefits_item</code> blocks.</EmptyState>;
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 420, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((it, i) => {
          const bg = it.background_image_url?.filename;
          return (
            <Box key={it._uid || i} {...editableProps(it)} sx={{ borderRadius: '14px', overflow: 'hidden', bgcolor: COLORS.cardBg, boxShadow: CARD_SHADOW }}>
              <Box sx={{ position: 'relative', height: 150, bgcolor: bg ? 'transparent' : '#E5E7EB', backgroundImage: bg ? `url(${bg})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'flex-end' }}>
                {it.video_url ? (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 80, px: 1, py: 0.25, fontSize: 11, fontFamily: 'Manrope, sans-serif' }}>▶ video</Box>
                ) : null}
                <Box sx={{ width: '100%', p: 1.25, background: 'linear-gradient(transparent, rgba(0,0,0,0.55))' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 15, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                    dangerouslySetInnerHTML={{ __html: safeHtml(it.header_text) || '(no header)' }} />
                </Box>
              </Box>
              <Box sx={{ p: 1.25 }}>
                <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                  dangerouslySetInnerHTML={{ __html: safeHtml(it.description_text) }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Sweepstakes Prizes preview ─────────────────────────────────────────
interface SweepstakesPrizeItemBlok extends StoryblokRel {
  component: 'sweepstakes_prize_item';
  name?: string;
  image?: StoryblokAsset;
  description?: string;
}
export interface SweepstakesPrizesContentBlok extends StoryblokRel {
  component: 'sweepstakes_prizes_content';
  items?: SweepstakesPrizeItemBlok[];
}
export interface SweepstakesPrizesPreviewProps { blok: SweepstakesPrizesContentBlok; }

export function SweepstakesPrizesPreview({ blok }: SweepstakesPrizesPreviewProps) {
  const items = blok.items ?? [];
  if (items.length === 0) return <EmptyState>No prizes yet. Add <code>sweepstakes_prize_item</code> blocks.</EmptyState>;
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Typography sx={{ maxWidth: 760, mx: 'auto', fontWeight: 800, fontSize: 18, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mb: 2 }}>Sweepstakes prizes</Typography>
      <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {items.map((it, i) => (
          <Box key={it._uid || i} {...editableProps(it)} sx={{ width: 220, bgcolor: COLORS.cardBg, borderRadius: '14px', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
            <Box sx={{ height: 140, bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {it.image?.filename
                ? <Box component="img" src={it.image.filename} alt={safeText(it.name)} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                : <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontStyle: 'italic' }}>no image</Typography>}
            </Box>
            <Box sx={{ p: 1.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(it.name) || '(no name)' }} />
              <Typography sx={{ fontSize: 12, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
                dangerouslySetInnerHTML={{ __html: safeHtml(it.description) }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Participating-Stores Modal preview ─────────────────────────────────
export interface ParticipatingStoresModalContentBlok extends StoryblokRel {
  component: 'participating_stores_modal_content';
  html_content?: Record<string, unknown> | string;
  header_text?: string | { type: string; content?: unknown[] };
  description_text?: string | { type: string; content?: unknown[] };
  cta_label?: string | { type: string; content?: unknown[] };
}
export interface ParticipatingStoresModalPreviewProps { blok: ParticipatingStoresModalContentBlok; }

export function ParticipatingStoresModalPreview({ blok }: ParticipatingStoresModalPreviewProps) {
  const html = resolveBodyHtml(blok.html_content);
  const title = blok.header_text;
  const description = blok.description_text;
  const ctaLabel = blok.cta_label;
  return (
    <Box sx={{ bgcolor: COLORS.overlay, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
      <Box {...editableProps(blok)} sx={{ bgcolor: COLORS.cardBg, borderRadius: '16px', boxShadow: CARD_SHADOW, width: '100%', maxWidth: 520, height: '85vh', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mb: 0.5, '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(title) }} />
        <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', mb: 1, '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(description) }} />
        {html ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <iframe srcDoc={withPreviewDocumentStyles(html)} style={{ width: '100%', height: '100%', border: 'none' }} title="participating stores" sandbox="allow-scripts" />
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontStyle: 'italic' }}>(no HTML content yet)</Typography>
        )}
        <Box sx={{ mt: 1.5, bgcolor: '#0D6EFD', borderRadius: '32px', py: 1.5, textAlign: 'center' }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 15, fontFamily: 'Manrope, sans-serif', '& p': { margin: 0 } }}
            dangerouslySetInnerHTML={{ __html: safeHtml(ctaLabel) }} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Rewards Carousel preview ───────────────────────────────────────────
interface RewardsCarouselTileBlok extends StoryblokRel {
  component: string;
  key?: string;
  rank?: number | string;
  cta_label?: string;
  body?: Record<string, unknown> | string;
  // sweepstakes tile fields
  chance_to_win?: string;
  entries_earned?: string;
  entry_earned?: string;
  next_drawing_date?: string;
  show_info_icon?: boolean;
}
export interface RewardsCarouselContentBlok extends StoryblokRel {
  component: 'rewards_carousel_content';
  items?: RewardsCarouselTileBlok[];
}
export interface RewardsCarouselPreviewProps { blok: RewardsCarouselContentBlok; }

export function RewardsCarouselPreview({ blok }: RewardsCarouselPreviewProps) {
  const tiles = [...(blok.items ?? [])].sort((a, b) => (Number(a.rank) || 0) - (Number(b.rank) || 0));
  if (tiles.length === 0) return <EmptyState>No tiles yet. Add <code>rewards_carousel_html_tile</code> / <code>rewards_carousel_sweepstakes_tile</code> blocks.</EmptyState>;
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 420, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tiles.map((t, i) => {
          const isSweeps = (t.key ?? '').toLowerCase() === 'sweepstakes' || !!t.chance_to_win;
          const bodyHtml = resolveBodyHtml(t.body);
          return (
            <Box key={t._uid || i} {...editableProps(t)} sx={{ bgcolor: COLORS.cardBg, borderRadius: '14px', boxShadow: CARD_SHADOW, p: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Box sx={{ bgcolor: COLORS.pageBg, color: COLORS.bodyText, borderRadius: 80, px: 1, py: 0.25, fontSize: 11, fontFamily: 'monospace' }}>{t.key || '(no key)'} · rank {t.rank ?? 0}</Box>
              </Box>
              {isSweeps ? (
                <Box sx={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t.chance_to_win && <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.headerText, '& p': { margin: 0 } }} dangerouslySetInnerHTML={{ __html: safeHtml(t.chance_to_win) }} />}
                  {t.entries_earned && <Typography sx={{ fontSize: 12, color: COLORS.bodyText, '& p': { margin: 0 } }} dangerouslySetInnerHTML={{ __html: safeHtml(t.entries_earned) }} />}
                  {t.next_drawing_date && <Typography sx={{ fontSize: 12, color: COLORS.bodyText, '& p': { margin: 0 } }} dangerouslySetInnerHTML={{ __html: safeHtml(t.next_drawing_date) }} />}
                </Box>
              ) : (
                <>
                  {bodyHtml
                    ? isFullHtmlDocument(bodyHtml)
                      ? (
                        <Box sx={{ width: '100%', height: 220, overflow: 'hidden' }}>
                          <iframe srcDoc={withPreviewDocumentStyles(bodyHtml)} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} title="rewards carousel tile body" sandbox="allow-scripts" />
                        </Box>
                      )
                      : <Box sx={{ fontFamily: 'Manrope, sans-serif', fontSize: 13, color: COLORS.bodyText, '& *': { m: 0 } }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    : <Typography sx={{ fontSize: 13, color: COLORS.copyrightText, fontStyle: 'italic' }}>(no body)</Typography>}
                </>
              )}
              {t.cta_label && (
                <Button variant="contained" disableElevation sx={{ mt: 1.25, bgcolor: COLORS.ctaBg, color: COLORS.ctaText, borderRadius: 80, textTransform: 'none', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>
                  <span dangerouslySetInnerHTML={{ __html: safeHtml(t.cta_label) }} />
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── App Download preview ───────────────────────────────────────────────
export interface AppDownloadContentBlok extends StoryblokRel {
  component: 'app_download_content';
  app_logo?: StoryblokAsset;
  description_text?: string;
  apple_app_store_url?: string;
  google_play_store_url?: string;
  apple_app_store_qr?: StoryblokAsset;
  google_play_store_qr?: StoryblokAsset;
}
export interface AppDownloadPreviewProps { blok: AppDownloadContentBlok; }

export function AppDownloadPreview({ blok }: AppDownloadPreviewProps) {
  return (
    <Box sx={{ bgcolor: COLORS.overlay, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
      <Box {...editableProps(blok)} sx={{ bgcolor: COLORS.cardBg, borderRadius: '16px', boxShadow: CARD_SHADOW, width: '100%', maxWidth: 420, p: 3, textAlign: 'center' }}>
        {blok.app_logo?.filename
          ? <Box component="img" src={blok.app_logo.filename} alt="app logo" sx={{ height: 64, mb: 1.5 }} />
          : <Box sx={{ height: 64, width: 64, mx: 'auto', mb: 1.5, borderRadius: '14px', bgcolor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: COLORS.copyrightText, fontStyle: 'italic' }}>logo</Box>}
        <Typography sx={{ fontSize: 14, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', mb: 2, '& p': { margin: 0 } }}
          dangerouslySetInnerHTML={{ __html: safeHtml(blok.description_text) || '(no description)' }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
          {[{ label: 'App Store', url: blok.apple_app_store_url, qr: blok.apple_app_store_qr }, { label: 'Google Play', url: blok.google_play_store_url, qr: blok.google_play_store_qr }].map((s) => (
            <Box key={s.label}>
              {s.qr?.filename
                ? <Box component="img" src={s.qr.filename} alt={`${s.label} QR`} sx={{ width: 96, height: 96, borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                : <Box sx={{ width: 96, height: 96, borderRadius: '8px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: COLORS.copyrightText, fontStyle: 'italic' }}>QR</Box>}
              <Typography sx={{ fontSize: 12, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mt: 0.5 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 10, color: s.url ? COLORS.ctaBg : COLORS.copyrightText, fontStyle: s.url ? 'normal' : 'italic', wordBreak: 'break-all' }}>{s.url || '(no link)'}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── Health Adventure preview (health_adventure_content) ──────────────────────

interface HealthAdventureBlok extends StoryblokRel {
  component: 'health_adventure';
  heading?: string | { type: string; content?: unknown[] };
  description?: string | { type: string; content?: unknown[] };
  icon?: StoryblokAsset;
  adventure_code?: string;
}

export interface HealthAdventureContentBlok extends StoryblokRel {
  component: 'health_adventure_content';
  adventures?: HealthAdventureBlok[];
}

export interface HealthAdventurePreviewProps { blok: HealthAdventureContentBlok; }

export function HealthAdventurePreview({ blok }: HealthAdventurePreviewProps) {
  const adventures = blok.adventures ?? [];
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Typography sx={{ fontWeight: 800, fontSize: 20, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mb: 2 }}>
        Health Adventures
      </Typography>
      {adventures.length === 0 && (
        <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', fontStyle: 'italic' }}>
          No adventure blocks found. Add health_adventure blocks in Storyblok.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {adventures.map((adv, i) => {
          const iconUrl = adv.icon?.filename ?? '';
          return (
            <Box
              key={adv._uid ?? i}
              {...editableProps(adv)}
              sx={{
                bgcolor: COLORS.cardBg,
                borderRadius: '16px',
                boxShadow: CARD_SHADOW,
                overflow: 'hidden',
                maxWidth: 480,
              }}
            >
              {/* Hero banner image */}
              {iconUrl ? (
                <Box
                  component="img"
                  src={iconUrl}
                  alt={safeText(adv.heading)}
                  sx={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box sx={{ width: '100%', height: 220, bgcolor: '#D0DCF0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 12, color: COLORS.copyrightText, fontFamily: 'Manrope, sans-serif', fontStyle: 'italic' }}>
                    No image
                  </Typography>
                </Box>
              )}
              {/* Card body */}
              <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 22, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', lineHeight: 1.25, mb: 0.75, '& p': { margin: 0 } }}
                  dangerouslySetInnerHTML={{ __html: safeHtml(adv.heading) || '<em style="color:#6B7280">No heading</em>' }} />
                {adv.description && (
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', lineHeight: 1.5, mb: 1.5, '& p': { margin: 0 } }}
                    dangerouslySetInnerHTML={{ __html: safeHtml(adv.description) }} />
                )}
                {/* Adventure code */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: COLORS.copyrightText, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'Manrope, sans-serif' }}>
                    Code
                  </Typography>
                  <Box sx={{ bgcolor: '#F0F4FA', borderRadius: '6px', px: 1, py: 0.25 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: adv.adventure_code ? COLORS.headerText : COLORS.copyrightText, fontStyle: adv.adventure_code ? 'normal' : 'italic' }}>
                      {adv.adventure_code || 'not mapped'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Terms & Conditions preview (terms_and_conditions_content + card variant) ──
export interface TermsAndConditionsContentBlok extends StoryblokRel {
  component: 'terms_and_conditions_content' | 'card_terms_and_conditions_content';
  agreement_page_code?: string;
}
export interface TermsAndConditionsPreviewProps { blok: TermsAndConditionsContentBlok; }

export function TermsAndConditionsPreview({ blok }: TermsAndConditionsPreviewProps) {
  const code = safeText(blok.agreement_page_code);
  const isCard = blok.component === 'card_terms_and_conditions_content';
  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box {...editableProps(blok)} sx={{ bgcolor: COLORS.cardBg, borderRadius: '14px', boxShadow: CARD_SHADOW, p: 3, maxWidth: 480, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16, color: COLORS.headerText, fontFamily: 'Manrope, sans-serif', mb: 1 }}>
          {isCard ? 'Card Terms & Conditions' : 'Terms & Conditions'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: COLORS.bodyText, fontFamily: 'Manrope, sans-serif', mb: 2 }}>
          The document body is resolved from the agreements lookup by page code — it isn&apos;t stored on this story.
        </Typography>
        <Box sx={{ display: 'inline-block', bgcolor: COLORS.pageBg, borderRadius: '8px', px: 1.5, py: 0.75 }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: code ? COLORS.headerText : COLORS.copyrightText, fontStyle: code ? 'normal' : 'italic' }}>
            {code || '(no agreement_page_code set)'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
